import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronRight, BarChart3 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Mailbox } from '@/hooks/useMailboxes';

interface MailboxAnalyticsSectionProps {
  mailboxes: Mailbox[];
  onFilterChange?: (filterType: string, filterValue: string) => void;
}

interface AnalyticsData {
  name: string;
  totalMailboxes: number;
  averageReputation: number;
  activeWarmup: number;
  dailyCapacity: number;
}

const getBarColor = (averageReputation: number) => {
  if (averageReputation >= 80) return '#10b981'; // green
  if (averageReputation >= 60) return '#f59e0b'; // yellow
  if (averageReputation > 0) return '#ef4444'; // red
  return '#6b7280'; // gray
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
        <p className="font-medium text-gray-900">{label}</p>
        <p className="text-sm text-gray-600">Mailboxes: {data.totalMailboxes}</p>
        <p className="text-sm text-gray-600">Active Warmup: {data.activeWarmup}</p>
        <p className="text-sm text-gray-600">Daily Capacity: {data.dailyCapacity.toLocaleString()}</p>
        <p className="text-sm text-gray-600">Avg Reputation: {data.averageReputation}%</p>
      </div>
    );
  }
  return null;
};

const AnalyticsChart = ({ data, title, onBarClick }: { data: AnalyticsData[]; title: string; onBarClick?: (name: string) => void }) => {
  if (!data || data.length === 0) {
    return (
      <div className="space-y-2">
        <h4 className="text-base font-semibold text-foreground">{title}</h4>
        <div className="flex items-center justify-center h-32 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <div className="text-center">
            <BarChart3 className="h-8 w-8 text-gray-400 mx-auto mb-1" />
            <p className="text-gray-500 text-sm font-medium">No data available</p>
            <p className="text-gray-400 text-xs">Configure mailboxes to see analytics</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-0">
      <h4 className="text-base font-semibold text-foreground mb-1 mt-3 pt-3 border-t border-gray-200">{title}</h4>
      <div className="h-80 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 80,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="name" 
              angle={-45}
              textAnchor="end"
              height={100}
              fontSize={12}
            />
            <YAxis 
              fontSize={12}
              label={{ value: 'Avg Reputation (%)', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar 
              dataKey="averageReputation" 
              radius={[4, 4, 0, 0]}
              onClick={(data) => onBarClick && onBarClick(data.name)}
              style={{ cursor: onBarClick ? 'pointer' : 'default' }}
              name="Average Reputation"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getBarColor(entry.averageReputation)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      {/* Data Summary Table */}
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-3 border w-full h-80">
        <div className="grid grid-cols-2 gap-3 h-full overflow-y-auto">
          {data.slice(0, 6).map((item, index) => (
            <div key={index} className="bg-white rounded-lg p-3 border shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                 onClick={() => onBarClick && onBarClick(item.name)}>
              <div className="text-sm font-semibold text-gray-900 truncate mb-2" title={item.name}>
                {item.name}
              </div>
              
              {/* Average Reputation - Prominent */}
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-600 font-medium">Avg Reputation</span>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <span className="text-sm font-bold text-green-700">{item.averageReputation}%</span>
                </div>
              </div>
              
              {/* Active Warmup */}
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-600 font-medium">Active Warmup</span>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                  <span className="text-sm font-bold text-blue-700">{item.activeWarmup}</span>
                </div>
              </div>
              
              {/* Mailbox Count */}
              <div className="text-xs text-gray-500 text-center bg-gray-50 rounded px-2 py-1">
                {item.totalMailboxes} mailbox{item.totalMailboxes !== 1 ? 'es' : ''}
              </div>
            </div>
          ))}
        </div>
        {data.length > 6 && (
          <div className="text-center mt-3">
            <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded">
              + {data.length - 6} more entries • Click chart bars or boxes to filter
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export function MailboxAnalyticsSection({ mailboxes, onFilterChange }: MailboxAnalyticsSectionProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Filter only successfully synced mailboxes for analytics
  const successfulSyncMailboxes = useMemo(() => {
    return mailboxes.filter(m => m.api_sync_status === 'SUCCESS');
  }, [mailboxes]);

  const providerAnalytics = useMemo(() => {
    const providerMap = new Map<string, { mailboxes: number; totalReputation: number; activeWarmup: number; capacity: number }>();

    successfulSyncMailboxes.forEach(mailbox => {
      const provider = mailbox.provider || 'Unknown';
      const reputation = mailbox.warmup_reputation ? parseInt(mailbox.warmup_reputation) : 0;
      const isActiveWarmup = mailbox.warmup_status === 'ACTIVE' ? 1 : 0;
      const capacity = mailbox.message_per_day || 0;

      if (!providerMap.has(provider)) {
        providerMap.set(provider, { mailboxes: 0, totalReputation: 0, activeWarmup: 0, capacity: 0 });
      }

      const current = providerMap.get(provider)!;
      current.mailboxes += 1;
      current.totalReputation += reputation;
      current.activeWarmup += isActiveWarmup;
      current.capacity += capacity;
    });

    return Array.from(providerMap.entries()).map(([name, data]) => ({
      name,
      totalMailboxes: data.mailboxes,
      averageReputation: data.mailboxes > 0 ? Number((data.totalReputation / data.mailboxes).toFixed(1)) : 0,
      activeWarmup: data.activeWarmup,
      dailyCapacity: data.capacity,
    })).sort((a, b) => b.averageReputation - a.averageReputation);
  }, [successfulSyncMailboxes]);

  const warmupStatusAnalytics = useMemo(() => {
    const statusMap = new Map<string, { mailboxes: number; totalReputation: number; activeWarmup: number; capacity: number }>();

    successfulSyncMailboxes.forEach(mailbox => {
      const status = mailbox.warmup_status || 'No Status';
      const reputation = mailbox.warmup_reputation ? parseInt(mailbox.warmup_reputation) : 0;
      const isActiveWarmup = mailbox.warmup_status === 'ACTIVE' ? 1 : 0;
      const capacity = mailbox.message_per_day || 0;

      if (!statusMap.has(status)) {
        statusMap.set(status, { mailboxes: 0, totalReputation: 0, activeWarmup: 0, capacity: 0 });
      }

      const current = statusMap.get(status)!;
      current.mailboxes += 1;
      current.totalReputation += reputation;
      current.activeWarmup += isActiveWarmup;
      current.capacity += capacity;
    });

    return Array.from(statusMap.entries()).map(([name, data]) => ({
      name,
      totalMailboxes: data.mailboxes,
      averageReputation: data.mailboxes > 0 ? Number((data.totalReputation / data.mailboxes).toFixed(1)) : 0,
      activeWarmup: data.activeWarmup,
      dailyCapacity: data.capacity,
    })).sort((a, b) => b.averageReputation - a.averageReputation);
  }, [successfulSyncMailboxes]);

  return (
    <Card className="w-full">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <BarChart3 className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg">Mailbox Analytics by Provider</CardTitle>
              </div>
              <Button variant="ghost" size="sm">
                {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </Button>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-2">
            <div className="text-sm text-muted-foreground">
              Analyze mailbox performance across different providers and warmup statuses.
            </div>
            
            {/* 2-Column Grid Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <AnalyticsChart 
                data={providerAnalytics} 
                title="Performance by Email Provider"
                onBarClick={(name) => onFilterChange && onFilterChange('provider', name)}
              />
              
              <AnalyticsChart 
                data={warmupStatusAnalytics} 
                title="Performance by Warmup Status"
                onBarClick={(name) => onFilterChange && onFilterChange('warmup_status', name)}
              />
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}